<!-- QuickAssignModal.vue -->
<template>
  <div
    class="modal fade"
    :id="`quickAssignModal-${seasonId}`"
    ref="quickAssignModalRef"
    tabindex="-1"
    aria-labelledby="quickAssignModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="quickAssignModalLabel">
            <i class="fa fa-bolt lightning-icon me-2"></i
            ><b>Quick Assign - {{ seasonName }}</b>
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label for="strategySelect" class="form-label">
              <b>Strategy to Assign:</b>
            </label>
            <select
              id="strategySelect"
              class="form-select"
              v-model="selectedStrategy"
            >
              <option value="">&#60;None&#62;</option>
              <option
                v-for="strategy in strategies"
                :key="strategy.id"
                :value="strategy.id"
              >
                {{ strategy.name }}
              </option>
            </select>
          </div>

          <!-- Owned Channels Section -->
          <div class="mb-3">
            <h6 class="channel-group-header">
              Your Channels
              <span class="float-end">
                Select:
                <a
                  href="#"
                  @click.prevent="selectAll('owned')"
                  class="select-link"
                  >All
                </a>
                |
                <a
                  href="#"
                  @click.prevent="selectNone('owned')"
                  class="select-link"
                >
                  None
                </a>
              </span>
            </h6>
            <div
              v-for="channelObj in ownedChannels"
              :key="channelObj.channel.id"
              class="form-check"
            >
              <input
                class="form-check-input"
                type="checkbox"
                :id="`ownedChannel-${channelObj.channel.id}`"
                v-model="selectedOwnedChannels"
                :value="channelObj.channel.id"
              />
              <label
                class="form-check-label text-start"
                :for="`ownedChannel-${channelObj.channel.id}`"
              >
                {{ channelObj.channel.description }}
              </label>
              <hr class="channel-separator" />
            </div>
          </div>

          <!-- Third Party Channels Section -->
          <div class="mb-3">
            <h6 class="channel-group-header">
              Third Party Channels
              <span class="float-end">
                Select:
                <a
                  href="#"
                  @click.prevent="selectAll('thirdParty')"
                  class="select-link"
                  >All
                </a>
                |
                <a
                  href="#"
                  @click.prevent="selectNone('thirdParty')"
                  class="select-link"
                  >None
                </a>
              </span>
            </h6>
            <div
              v-for="channelObj in thirdPartyChannels"
              :key="channelObj.channel.id"
              class="form-check"
            >
              <input
                class="form-check-input"
                type="checkbox"
                :id="`thirdPartyChannel-${channelObj.channel.id}`"
                v-model="selectedThirdPartyChannels"
                :value="channelObj.channel.id"
              />
              <label
                class="form-check-label text-start"
                :for="`thirdPartyChannel-${channelObj.channel.id}`"
              >
                {{ channelObj.channel.description }}
              </label>
              <hr class="channel-separator" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="applyAssignment"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { Modal } from 'bootstrap';

export default {
  name: 'QuickAssignModal',
  props: {
    seasonId: {
      type: Number,
      required: true,
    },
    seasonName: {
      type: String,
      required: true,
    },
    strategies: {
      type: Array,
      default: () => [],
    },
    ownedChannels: {
      type: Array,
      default: () => [],
    },
    thirdPartyChannels: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const selectedStrategy = ref('');
    const selectedOwnedChannels = ref([]);
    const selectedThirdPartyChannels = ref([]);
    const quickAssignModalRef = ref(null);

    const selectAll = (type) => {
      if (type === 'owned') {
        selectedOwnedChannels.value = props.ownedChannels.map(
          (channelObj) => channelObj.channel.id
        );
      } else {
        selectedThirdPartyChannels.value = props.thirdPartyChannels.map(
          (channelObj) => channelObj.channel.id
        );
      }
    };

    const selectNone = (type) => {
      if (type === 'owned') {
        selectedOwnedChannels.value = [];
      } else {
        selectedThirdPartyChannels.value = [];
      }
    };

    const applyAssignment = () => {
      emit('apply-assignment', {
        strategy: Number(selectedStrategy.value), // Convert to number if needed
        ownedChannels: selectedOwnedChannels.value,
        thirdPartyChannels: selectedThirdPartyChannels.value,
      });

      // Step 2: Hide the modal after clicking "Apply"
      if (quickAssignModalRef.value) {
        const modalInstance = Modal.getOrCreateInstance(
          quickAssignModalRef.value
        );
        modalInstance.hide();
      }
    };

    return {
      selectedStrategy,
      selectedOwnedChannels,
      selectedThirdPartyChannels,
      selectAll,
      selectNone,
      applyAssignment,
      quickAssignModalRef,
    };
  },
};
</script>

<style scoped>
.modal-dialog {
  max-width: 500px;
}

.form-check-label {
  text-align: left;
  width: 100%;
  margin-left: 8px;
  display: inline-block;
}

.channel-group-header {
  background-color: #618183;
  color: #ffffff;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 10px;
}

.select-link {
  color: #ffffff;
  text-decoration: underline;
}

.select-link:hover {
  color: #d1e7dd;
}

.form-check-input {
  margin-top: 0.3rem;
}

.channel-separator {
  border: 0;
  border-top: 1px solid #dcdcdc;
  margin: 10px 0;
}
</style>
